import { DANCE_STYLE } from "@dw/models/danceClass";
import { IGeoPoint, IMenuItem } from "@dw/models/shared";

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again later.';

export const QUERY_CLIENT_STALE_TIME = 1000 * 60 * 60; // 60 Minutes

// Defaults to Spark Arena, Auckland, NZ
export const DEFAULT_MAP_CENTER: IGeoPoint = {
  latitude: -36.8477629,
  longitude: 174.7765824,
};

export const ROUTES = {
  Root: '/',
  Login: '/login',

  Dashboard: '/dashboard',
  ManageDanceSchool: '/dashboard/dance-school',
  ManageLocation: '/dashboard/locations/:locationId',
  ManageMyProfile: '/dashboard/my-profile',


  CreateStudent: '/dashboard/students/create-new',
  ManageStudent: '/dashboard/students/:studentId',

  ViewDancerAccount: '/dashboard/accounts/:accountId',

  ViewCommunicationDetails: '/dashboard/communication/:messageId',

  RegisterTrialClass: '/dashboard/trials/register-new',

  BulkInvoices: '/dashboard/bulk-invoices',
};

export enum MANAGE_DANCE_SCHOOL_LOCATION_TAB {
  STUDIOS = 'studios',
  SCHEDULE = 'schedule',
  CLASSES = 'classes',
  STUDENTS = 'location-students'
}

export enum MANAGE_DANCE_SCHOOL_STUDENTS_TAB {
  STUDENT_LIST = 'student-list',
  ENROLMENTS = 'enrolments',
  TRIALS = 'trials',
  ACCOUNTS = 'accounts',
  INVOICES = 'invoices'
}

export enum MANAGE_DANCE_SCHOOL_TAB {
  LOCATIONS = 'locations',
  STUDENTS = 'students',
  SETTINGS = 'settings',
  STAFF = 'staff',
  BUSINESS_METRICS = 'business-metrics',
  COMMUNICATION = 'communication',
}

export enum MANAGE_DANCE_SCHOOL_SETTINGS_TAB {
  PUBLIC_INFO = 'public-info',
  ENROLMENT_SETTINGS = 'enrolment-settings',
  EMAIL_SETTINGS = 'email-settings',
  INVOICE_SETTINGS = 'invoice-settings',
}

export const APP_TABS_LABELS: Record<MANAGE_DANCE_SCHOOL_LOCATION_TAB | MANAGE_DANCE_SCHOOL_TAB | MANAGE_DANCE_SCHOOL_STUDENTS_TAB | MANAGE_DANCE_SCHOOL_SETTINGS_TAB, string> = {
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDIOS]: 'Studios',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.SCHEDULE]: 'Schedule',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.CLASSES]: 'Classes',
  [MANAGE_DANCE_SCHOOL_LOCATION_TAB.STUDENTS]: 'Students',

  [MANAGE_DANCE_SCHOOL_TAB.LOCATIONS]: 'Locations',
  [MANAGE_DANCE_SCHOOL_TAB.STUDENTS]: 'Students',
  [MANAGE_DANCE_SCHOOL_TAB.SETTINGS]: 'Settings',
  [MANAGE_DANCE_SCHOOL_TAB.STAFF]: 'Staff',
  [MANAGE_DANCE_SCHOOL_TAB.BUSINESS_METRICS]: 'Business Metrics',
  [MANAGE_DANCE_SCHOOL_TAB.COMMUNICATION]: 'Communication',

  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.STUDENT_LIST]: 'Student List',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ENROLMENTS]: 'Enrolments',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.TRIALS]: 'Trials',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.ACCOUNTS]: 'Accounts',
  [MANAGE_DANCE_SCHOOL_STUDENTS_TAB.INVOICES]: 'Invoices',

  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.PUBLIC_INFO]: 'Public Info',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.EMAIL_SETTINGS]: 'Email Settings',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.ENROLMENT_SETTINGS]: 'Enrolment Settings',
  [MANAGE_DANCE_SCHOOL_SETTINGS_TAB.INVOICE_SETTINGS]: 'Invoice Settings',
}

export enum DAY_OF_WEEK {
  MONDAY = 'Monday',
  TUESDAY = 'Tuesday',
  WEDNESDAY = 'Wednesday',
  THURSDAY = 'Thursday',
  FRIDAY = 'Friday',
  SATURDAY = 'Saturday',
  SUNDAY = 'Sunday',
}
export const DAY_OF_WEEK_NUMBERS: Record<DAY_OF_WEEK, number> = {
  [DAY_OF_WEEK.SUNDAY]: 0,
  [DAY_OF_WEEK.MONDAY]: 1,
  [DAY_OF_WEEK.TUESDAY]: 2,
  [DAY_OF_WEEK.WEDNESDAY]: 3,
  [DAY_OF_WEEK.THURSDAY]: 4,
  [DAY_OF_WEEK.FRIDAY]: 5,
  [DAY_OF_WEEK.SATURDAY]: 6
}

const splitByUpperCase = (input?: string): string => !input ? '' : (
  `${input.replace(/([A-Z](?=[a-z]+)|[A-Z]+(?![a-z]))/g, ' $1').slice(1)}`
);
export const DAY_OF_WEEK_MENU_OPTIONS: IMenuItem[] = Object.values(DAY_OF_WEEK).map((x) => ({
  value: x,
  text: splitByUpperCase(x)
}) as IMenuItem);
export const DANCE_STYLE_MENU_OPTIONS: IMenuItem[] = Object.values(DANCE_STYLE).map((x) => ({
  value: x,
  text: splitByUpperCase(x)
}) as IMenuItem);

export enum DATE_FORMATS {
  FullDateTime = 'D MMM YYYY hh:mma',

  DayMonthYear = 'D MMM YYYY',

  DayMonthYearWithFullDay = 'dddd, D MMM YYYY',

  DayMonth = 'D MMM',

  Day = 'D',

  DayOfWeek = 'ddd',

  Hour = 'h a',

  HourMinute = 'h:mma',

  HourMinuteSecond = 'hh:mm:ssa',

  HourMinuteSecond24Hour = 'HH:mm:ss',

  DayMonthHour = 'D MMM hh:mma',

  YearMonthDayIso = 'YYYY-MM-DD'
};

export enum THIRD_PARTY_PROVIDER {
  XERO = "Xero",
  GOOGLE = "Google",
  FACEBOOK = "Facebook",
  APPLE = "Apple"
}

export enum SCREEN_SIZE {
  XS = 0,
  SM = 1,
  MD = 2,
  LG = 3,
  XL = 4
}

export enum STORAGE_KEYS {
  AcceptInvite = 'dw_accept_invite_details',
  UseBusinessMetricsV2 = 'dw_use_business_metrics_v2',
  ShowEnrolmentEarningsExcludingTax = 'dw_show_enrolment_earnings_excluding_tax',
}

export enum SESSION_STORAGE_KEYS {
  InvoiceFilters = 'InvoiceFilters',
  CalendarDateRangeStart = 'CalendarDateRangeStart',
  DanceClassesScheduleFilter = 'DanceClassesScheduleFilter',
  ActiveEnrolmentYear = 'ActiveEnrolmentYear'
}